import { useEnum } from "@/api/services/global/enum";
import { updateLanguageTestApi, useGetLanguageTestList } from "@/api/services/global/master";
import { TMasterLanguageTestLevel } from "@/api/entities/master-language-test-level.entity";
import { TGetLanguageTestListOutputDto } from "@/api/services/global/master/dtos/get-master-language-test-list.dto";
import { IconOrderTable } from "@/assets/icons/IconOrderTable";
import { ConfirmLeavePage } from "@/components/commons/ConfirmLeavePage";
import { FooterActionBar } from "@/components/commons/FooterActionBar";
import { Button } from "@/components/elements/Button";
import { InnerBox } from "@/components/elements/InnerBox";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { Switch } from "@/components/elements/Switch";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { DATE_TIME_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { HiddenLanguageTestModal } from "@/features/master-data/components/LanguageTest/HiddenLanguageTestModal";
import { LanguageTestFormModal } from "@/features/master-data/components/LanguageTest/LanguageTestFormModal";
import { MASTER_DATA_TYPE_TITLE } from "@/features/master-data/constants/masterDataType";
import { sortLanguageTestLevels } from "@/features/master-data/utils/sortLanguageTestLevels";
import { useVisible } from "@/hooks/useVisible";
import { AppRoutes } from "@/routes/config";
import { background, blue, button, divider, green, text } from "@/theme/colors";
import { rounded, spacing } from "@/theme/variables";
import { dayjs } from "@/utils/dayjs";
import { findLabelByValue } from "@/utils/object";
import { showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { Box, Stack, styled, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { Link } from "react-router-dom";

// Each level is one sub-row of fixed height so レベル and 育成就労日本語要件判断指標 stay aligned.
const SUB_ROW_HEIGHT = 40;

const SUB_ROW_BORDER = `1px solid ${divider.middle}`;

export const MasterDataLanguageTest = () => {
  const formModal = useVisible();
  const [hiddenLanguageTest, setHiddenLanguageTest] = useState<TGetLanguageTestListOutputDto>();
  const [selectedLanguageTest, setSelectedLanguageTest] = useState<TGetLanguageTestListOutputDto>();
  const [enableSaveOrderBtn, setEnableOrderBtn] = useState<boolean>(false);

  const { data, isLoading } = useGetLanguageTestList();
  const { data: indicatorOptions } = useEnum("EIkuseiJapaneseRequirementIndicator");

  const [rows, setRows] = useState<TGetLanguageTestListOutputDto[]>(data);

  useEffect(() => {
    if (data && data.length > 0) setRows(data);
  }, [data]);

  const handleSaveOrder = async () => {
    try {
      const rs = await updateLanguageTestApi({
        languageTests: rows.map((item) => ({
          id: toNumber(item.id),
          languageTestName: item.languageTestName,
          ckLanguageTestRenewVisaCsvCode: item.ckLanguageTestRenewVisaCsvCode,
        })),
      });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      setEnableOrderBtn(false);
    } catch (error) {
      showError(error);
    }
  };

  // Handle row dragging
  const onDragEnd = (result: DropResult) => {
    if (!result.destination || !result.source || result.source.index === result.destination.index) return;
    const reorderedRows = Array.from(rows);
    const [removed] = reorderedRows.splice(result.source.index, 1);
    reorderedRows.splice(result.destination.index, 0, removed);
    setRows(reorderedRows);
    setEnableOrderBtn(true);
  };

  const renderLevelSubRows = (row: TGetLanguageTestListOutputDto, getValue: (level: TMasterLanguageTestLevel) => string | null | undefined) => {
    const levels: (TMasterLanguageTestLevel | undefined)[] = row.languageTestLevels?.length
      ? sortLanguageTestLevels(row.languageTestLevels)
      : [undefined];
    return levels.map((level, index) => {
      const value = (level && getValue(level)) || EMPTY_STR.TEXT;
      return (
        <Box
          key={level?.id ?? index}
          sx={{
            height: `${SUB_ROW_HEIGHT}px`,
            display: "flex",
            alignItems: "center",
            px: 1,
            borderBottom: index !== levels.length - 1 ? SUB_ROW_BORDER : "none",
          }}
        >
          {/* noWrap keeps every sub-row at SUB_ROW_HEIGHT so both level columns stay aligned */}
          <Typography color={row.isHidden ? text.tertiary : text.primary} variant="body14" noWrap title={value} sx={{ minWidth: 0 }}>
            {value}
          </Typography>
        </Box>
      );
    });
  };

  if (isLoading || rows.length === 0) return <LoadingPage />;

  return (
    <>
      <TopPage title={"「" + MASTER_DATA_TYPE_TITLE.LANGUAGE_TEST + "」の編集"} enablePageBack={false} />
      <InnerBox>
        <Stack direction="row" justifyContent="flex-end" mb={3}>
          <Button size="sm" variant="primary" onClick={() => formModal.open()}>
            項目追加
          </Button>
        </Stack>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="tableRows" direction="vertical">
            {(provided) => (
              <StyledTable {...provided.droppableProps} ref={provided.innerRef}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell>試験名（日本語）</TableCell>
                      <TableCell>期間更新CSVのコード（列CK）</TableCell>
                      <TableCell>レベル</TableCell>
                      <TableCell>育成就労日本語要件判断指標</TableCell>
                      <TableCell>最終更新日</TableCell>
                      <TableCell>有効／無効</TableCell>
                      <TableCell>編集</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, index) => (
                      <Draggable key={row.id} draggableId={row.id.toString()} index={index}>
                        {(provided) => (
                          <TableRow ref={provided.innerRef} {...provided.draggableProps}>
                            <TableCell sx={{ width: "3%" }} {...provided.dragHandleProps}>
                              <IconOrderTable color={blue[80]} />
                            </TableCell>
                            <TableCell sx={{ width: "22%" }}>
                              <Typography color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                                {row.languageTestName || EMPTY_STR.TEXT}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ width: "15%" }}>
                              <Typography color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                                {row.ckLanguageTestRenewVisaCsvCode || EMPTY_STR.TEXT}
                              </Typography>
                            </TableCell>
                            <TableCell className="sub-row-cell" sx={{ width: "10%" }}>
                              {renderLevelSubRows(row, (level) => level.level)}
                            </TableCell>
                            <TableCell className="sub-row-cell" sx={{ width: "18%" }}>
                              {renderLevelSubRows(row, (level) => findLabelByValue(indicatorOptions, level.ikuseiJapaneseRequirementIndicator))}
                            </TableCell>
                            <TableCell sx={{ width: "14%" }}>
                              <Typography color={row.isHidden ? text.tertiary : text.primary} sx={{ px: 0.5 }} variant="body14">
                                {row.updatedAt ? dayjs(row.updatedAt).format(DATE_TIME_FORMAT) : EMPTY_STR.DATE}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ width: "10%" }} align="center">
                              <Switch
                                sx={{
                                  marginRight: 0,
                                  marginLeft: 0,
                                }}
                                checked={!row.isHidden}
                                onChange={() => setHiddenLanguageTest(row)}
                                color="primary"
                              />
                            </TableCell>
                            <TableCell sx={{ width: "8%" }}>
                              <Button
                                size="sm"
                                onClick={() => {
                                  setSelectedLanguageTest(row);
                                  formModal.open();
                                }}
                              >
                                編集
                              </Button>
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableBody>
                </Table>
              </StyledTable>
            )}
          </Droppable>
        </DragDropContext>
      </InnerBox>
      <FooterActionBar mt={4}>
        <Stack
          sx={{
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Button component={Link} to={AppRoutes.masterData} variant="ghost" size="sm">
            戻る
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleSaveOrder()} disabled={!enableSaveOrderBtn}>
            オーダーを保存する
          </Button>
        </Stack>
      </FooterActionBar>
      {formModal.visible && (
        <LanguageTestFormModal
          onClose={() => {
            formModal.close();
            setSelectedLanguageTest(undefined);
          }}
          isOpen={formModal.visible}
          selectedLanguageTest={selectedLanguageTest}
        />
      )}

      <HiddenLanguageTestModal hiddenLanguageTest={hiddenLanguageTest} setHiddenLanguageTest={setHiddenLanguageTest} />
      <ConfirmLeavePage shouldBlock={enableSaveOrderBtn}></ConfirmLeavePage>
    </>
  );
};

const StyledTable = styled(Box)`
  border-radius: ${rounded.sm};
  overflow: auto;
  max-height: calc(100vh - 380px);
  border: 1px solid ${divider.middle};
  background-color: ${background.white};
  * {
    box-shadow: none;
  }
  // Grid borders between columns, same as the 特定産業分野 table
  th,
  td {
    &:not(:first-of-type) {
      border-left: 1px solid ${divider.middle};
    }
  }
  .MuiToolbar-dense {
    display: none;
  }
  .MuiTableCell-head {
    padding-top: 8px !important;
    padding-bottom: 8px !important;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    text-align: left;
    position: sticky;
    background-color: white;
    z-index: 500;
    top: 0;
    // Collapsed borders don't stick with the header, so draw the bottom separator with a shadow instead
    border-bottom: none;
    box-shadow: inset 0 -1px 0 ${divider.middle};
  }
  .Mui-TableHeadCell-Content-Labels {
    > span {
      margin-left: 4px;
      img {
        opacity: 0.2;
      }
      .MuiBadge-badge {
        display: none;
      }
    }
  }
  .MuiTableCell-body {
    font-size: 14px;
    font-weight: 400;
    line-height: 28px;
  }

  // Default
  .MuiTableRow-root {
    background-color: ${background.white};
    box-shadow: none;
    cursor: initial;
    .MuiTableCell-root {
      padding: ${spacing.xxs} ${spacing.sm};
      border-color: ${divider.middle};
      opacity: 1;
    }
    &:not(.MuiTableRow-head):last-child {
      .MuiTableCell-root {
        border-bottom: 0;
      }
    }
  }
  // Hover
  .MuiTableRow-root:not(.MuiTableRow-head):hover {
    &,
    td {
      background-color: ${background.white};
      &::after {
        background-color: ${background.primary};
      }
    }
  }
  // Click/Pressed
  .Mui-selected {
    background-color: ${background.white} !important;
    td::after {
      background: ${background.secondary}!important;
    }
  }

  .MuiPaper-root {
    box-shadow: none;
  }
  .MuiPaper-root > .MuiBox-root {
    display: none;
  }

  .MuiCheckbox-root {
    color: ${button.primary}!important;
    height: 24px;
    width: 24px;
    padding: 0;
  }

  .MuiTableCell-root {
    padding: 4px 8px !important;
  }

  // Sub-row cells draw their own separators edge to edge
  .MuiTableCell-root.sub-row-cell {
    padding: 0 !important;
  }

  // Disabled Row
  .MuiTableCell-root.disabled {
    opacity: 1;
    background: ${background.disable}!important;
    pointer-events: none;
  }

  // added row
  .MuiTableCell-root.added {
    background: ${green[99]}!important;
    &::after {
      background: unset !important;
    }
  }

  // Failed row
  .MuiTableCell-root.failed {
    background: ${background.error}!important;
    &::after {
      background: unset !important;
    }
  }

  // Clickable row
  .MuiTableCell-root.clickable {
    cursor: pointer;
  }
`;
